import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, XCircle } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { formatEuros, formatDate } from "@/lib/format";
import { EtapeMenu } from "@/components/fiche/EtapeMenu";
import { MotifDialog } from "@/components/fiche/MotifDialog";
import { DEGRADE } from "./commun";

export const suivanteDe = (etapes, code) => {
  const e = etapes.find((x) => x.code === code);
  if (!e || e.sortie) return null;
  if (e.suivante) return etapes.find((x) => x.code === e.suivante) || null;
  const normales = etapes.filter((x) => x.sortie !== "perdu");
  return normales[normales.findIndex((x) => x.code === code) + 1] || null;
};

const Carte = ({ f, etape, etapes, onDeplacer }) => {
  const suiv = f.statut === "en_cours" ? suivanteDe(etapes, f.etape) : null;
  const pa = f.prochaine_action;
  return (
    <div draggable onDragStart={(ev) => ev.dataTransfer.setData("text/plain", f.id)} data-testid={`thermo-card-${f.id}`}
      className="at-fiche p-3 space-y-2 transition-transform duration-200 hover:-translate-y-0.5"
      style={{ border: f.en_retard ? "2px dashed var(--danger)" : `2px solid ${etape.temperature || "var(--muted)"}` }}>
      <Link to={`/fiches/${f.id}`} className="block" data-testid={`thermo-card-link-${f.id}`}>
        <div className="flex items-start justify-between gap-2">
          <p className="font-bold truncate">{f.nom}</p>
          <p className="font-bold whitespace-nowrap text-sm" data-testid={`thermo-card-amount-${f.id}`}>{formatEuros(f.montant_estime)}</p>
        </div>
        <p className="text-xs at-muted truncate">{[f.commune, f.operation].filter(Boolean).join(" · ")}</p>
        <div className="mt-2 flex items-center gap-2" data-testid={`thermo-gauge-${f.id}`}>
          <span className="flex-1 h-1.5 at-pill overflow-hidden" style={{ background: "var(--surface-2)" }}>
            <span className="block h-full at-pill" style={{ width: `${f.degres}%`, background: etape.temperature || "var(--muted)" }} />
          </span>
          <span className="text-xs font-bold">{f.degres}°</span>
        </div>
        <p className="text-xs font-semibold mt-2 truncate" style={{ color: f.sans_action ? "var(--danger)" : "var(--muted)" }} data-testid={`thermo-next-${f.id}`}>
          {pa ? `${pa.type_label} · ${formatDate(pa.echeance)}` : f.sans_action ? "Sans action" : f.statut_label}
        </p>
      </Link>
      <div className="flex gap-2">
        {suiv && suiv.autorisee && <button type="button" onClick={() => onDeplacer(f.id, suiv.code)} className="at-btn at-btn-primary flex-1 !px-3 text-sm" data-testid={`thermo-advance-${f.id}`}>Avancer →</button>}
        <EtapeMenu etapes={etapes} actuelle={f.etape} onChoisir={(code, m) => onDeplacer(f.id, code, m)} testId={`thermo-move-${f.id}`} label="Déplacer" className="flex-1 !px-3 text-sm" />
      </div>
    </div>
  );
};

const ZoneSortie = ({ etape, icone: Icone, couleur, onDrop, testId }) => {
  const [survol, setSurvol] = useState(false);
  if (!etape) return null;
  return (
    <div onDragOver={(ev) => { ev.preventDefault(); setSurvol(true); }} onDragLeave={() => setSurvol(false)} onDrop={(ev) => { setSurvol(false); onDrop(ev, etape); }}
      className="flex-1 min-h-[56px] at-soft flex items-center justify-center gap-2 font-bold text-sm transition-colors duration-200"
      style={{ border: `2px dashed ${couleur}`, color: couleur, background: survol ? `color-mix(in srgb, ${couleur} 14%, transparent)` : undefined }} data-testid={testId}>
      <Icone size={18} />Déposer ici : {etape.nom}
    </div>
  );
};

export const PipelineThermo = ({ proc, fiches, role, onMaj }) => {
  const [survol, setSurvol] = useState(null);
  const [motif, setMotif] = useState(null);
  const etapes = proc.etapes.map((e) => ({ ...e, autorisee: e.profils.includes(role) }));
  const colonnes = etapes.filter((e) => !e.sortie || e.sortie === "gagne");
  const deplacer = async (id, code, texte) => {
    try { await api.post(`/fiches/${id}/etape`, { etape: code, motif: texte }); toast.success("Étape mise à jour."); onMaj(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  const deposer = (ev, etape) => {
    ev.preventDefault(); setSurvol(null);
    const id = ev.dataTransfer.getData("text/plain");
    if (!id || fiches.find((f) => f.id === id)?.etape === etape.code) return;
    etape.sortie === "perdu" ? setMotif({ id, etape }) : deplacer(id, etape.code);
  };
  return (
    <div data-testid="view-pipeline-thermometre">
      <div className="h-2.5 at-pill mb-3" style={{ background: DEGRADE }} data-testid="thermo-band" />
      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 md:mx-0 md:px-0 overscroll-x-contain" data-testid="kanban-board">
        {colonnes.map((e) => {
          const lot = fiches.filter((f) => f.etape === e.code);
          return (
            <section key={e.code} onDragOver={(ev) => { ev.preventDefault(); setSurvol(e.code); }} onDragLeave={() => setSurvol(null)} onDrop={(ev) => deposer(ev, e)}
              className="snap-start shrink-0 w-[85vw] sm:w-[300px] at-soft p-3 transition-colors duration-200" style={survol === e.code ? { background: "var(--line)" } : undefined} data-testid={`kanban-column-${e.code}`}>
              <header className="mb-3 px-1">
                <span className="block h-1.5 at-pill mb-2" style={{ background: e.temperature || "var(--muted)" }} />
                <div className="flex items-center gap-2">
                  <p className="font-bold flex-1 truncate">{e.nom}</p>
                  <span className="text-sm at-muted font-semibold" data-testid={`kanban-column-count-${e.code}`}>{lot.length} · {formatEuros(lot.reduce((s, f) => s + (f.montant_estime || 0), 0))}</span>
                </div>
                {e.duree_max_heures && <p className="text-xs at-muted mt-1">Délai max : {e.duree_max_heures >= 48 ? `${Math.round(e.duree_max_heures / 24)} j` : `${e.duree_max_heures} h`}</p>}
              </header>
              <div className="space-y-2 min-h-[80px]">{lot.map((f) => <Carte key={f.id} f={f} etape={e} etapes={etapes} onDeplacer={deplacer} />)}</div>
            </section>
          );
        })}
      </div>
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
        <ZoneSortie etape={etapes.find((e) => e.sortie === "gagne")} icone={Trophy} couleur="var(--temp-7)" onDrop={deposer} testId="drop-zone-won" />
        {etapes.filter((e) => e.sortie === "perdu").map((e) => <ZoneSortie key={e.code} etape={e} icone={XCircle} couleur="var(--danger)" onDrop={deposer} testId={`drop-zone-lost-${e.code}`} />)}
      </div>
      <MotifDialog etape={motif?.etape} onFermer={() => setMotif(null)} onValider={(code, m) => { deplacer(motif.id, code, m); setMotif(null); }} />
    </div>
  );
};
